import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { DraftLoanApplicationWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import {
	Box,
	Chip,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllCustomersService } from "../../../../utils/api/customerservice/customerlist/retrieveAllCustomers";
import { retrieveAllLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveAllLoanProduct";
import { BaseButton } from "../../../../components/button/styled";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../../assets";
import { useNavigate } from "react-router-dom";
import { draftLoanApplicationService } from "../../../../utils/api/loanmanagement/loansetup/draftLoanApplicationService";
import { retrieveLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveLoanProduct";
import { retrieveSavingsAccountByCustomerIdService } from "../../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";
import { retrieveLoanIndexService } from "../../../../utils/api/loanmanagement/loansetup/retrieveLoanIndex";
import { formatAmountDisplay } from "../../../../helpers/formatAmountDisplay";

export const DraftLoanApplication = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedLoanProduct, setSelectedLoanProduct] = useState<Record<
		string,
		any
	> | null>(null);
	const [formDetails, setFormDetails] = useState({
		obligor: " ",
		disbursementAccount: "",
		loanProduct: " ",
		loanId: "",
		term: "",
		interestRate: "",
		disbursementDate: "",
		firstRepaymentDate: "",
		appliedAmount: "",
		// penalty: "",
		attachments: [],
		description: "",
	});

	useEffect(() => {
		setActiveNavItem("Loan Management");
	}, [setActiveNavItem]);

	useEffect(() => {
		setSelectedLoanProduct(null);
		const fetchLoanProduct = async (loanProductId: string) => {
			try {
				if (!formDetails.loanProduct.trim()) return;
				const response = await retrieveLoanProductService(TOKEN, loanProductId);
				if (response) {
					setSelectedLoanProduct(response);
				} else {
					setSelectedLoanProduct(null);
				}
			} catch (error) {
				console.error("Failed to fetch loan product:", error);
			}
		};
		fetchLoanProduct(formDetails.loanProduct);
	}, [TOKEN, formDetails.loanProduct]);

	useEffect(() => {
		setFormDetails((prev) => ({ ...prev, disbursementAccount: "" }));
		const fetchObligorSavingsAccount = async (obligorId: string) => {
			try {
				if (!formDetails.obligor.trim()) return;
				const response = await retrieveSavingsAccountByCustomerIdService(
					TOKEN,
					obligorId,
				);
				return setFormDetails((prev) => ({
					...prev,
					disbursementAccount: response.accountNumber,
				}));
			} catch (error) {
				return console.error("Failed to fetch obligor savings account:", error);
			}
		};
		fetchObligorSavingsAccount(formDetails.obligor);
	}, [TOKEN, formDetails.obligor]);

	useEffect(() => {
		retrieveLoanIndexService(TOKEN)
			.then((data) => {
				return setFormDetails((prev) => ({ ...prev, loanId: data.nextIndex }));
			})
			.catch((err) => {
				console.error("Failed to fetch loan index:", err);
			});
	}, [TOKEN]);

	const { data: customers } = useQuery({
		queryKey: ["customers", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllCustomersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: loanProducts } = useQuery({
		queryKey: ["loanProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllLoanProductService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const handleChange = (
		e:
			| SelectChangeEvent<unknown>
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, type, value } = e.target as HTMLInputElement;
		if (type === "file") {
			const input = e.target as HTMLInputElement;
			const fileArray = input.files ? Array.from(input.files) : [];
			setFormDetails((prev) => ({
				...prev,
				[name]: [...(prev[name as keyof typeof prev] as File[]), ...fileArray],
			}));
		} else {
			setFormDetails((prev) => ({
				...prev,
				[name]: value,
				...(name === "loanProduct" && {
					term: "",
					interestRate: "",
					appliedAmount: "",
				}),
			}));
		}
	};

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleDeleteAttachment = (fileToDelete: File) => {
		setFormDetails((prev) => {
			const updated = prev.attachments.filter((file) => file !== fileToDelete);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			return {
				...prev,
				attachments: updated,
			};
		});
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		const formData = new FormData();
		const data = {
			obligor: formDetails.obligor,
			disbursementAccount: formDetails.disbursementAccount,
			loanProduct: formDetails.loanProduct,
			loanId: formDetails.loanId,
			term: formDetails.term,
			interestRate: formDetails.interestRate,
			disbursementDate: formDetails.disbursementDate,
			firstRepaymentDate: formDetails.firstRepaymentDate,
			appliedAmount: formDetails.appliedAmount,
			// penalty: formDetails.penalty,
			description: formDetails.description,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		formDetails.attachments.forEach((file, index) => {
			formData.append("loanApplicationAttachment", file);
		});
		try {
			const response = await draftLoanApplicationService(TOKEN, formData);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to save loan application. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to save loan application.${error.message} `);
			console.error("Failed to save loan application:", error);
		}
	};

	return (
		<AppLayout pageId={"Book-Loan"} pageTitle={"Loan Application"}>
			<DraftLoanApplicationWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title="Loan Application Saved Successfully"
					message="Your information has been successfully added to the system."
					callToAction="Back to Loan Application List"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required information to apply for a loan
					</Typography>
				</Box>
				<form onSubmit={handleSubmit}>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Obligor</BaseLabel>
								<BaseSelect
									name="obligor"
									fontweight={400}
									value={formDetails.obligor}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Customer</BaseOption>
									{customers?.map(
										(customer: Record<string, any>, index: number) => (
											<BaseOption key={index} value={customer.id}>
												{`${customer?.firstName} ${customer?.lastName} `
													?.split(" ")
													.map(
														(word: string) =>
															word.charAt(0).toUpperCase() + word.slice(1),
													)
													.join(" ")}
											</BaseOption>
										),
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Disbursement Account Number
								</BaseLabel>
								<BaseInput
									required
									readOnly
									name="disbursementAccount"
									value={formDetails.disbursementAccount}
									placeholder="Obligor Account Number"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Loan Product</BaseLabel>
								<BaseSelect
									name="loanProduct"
									fontweight={400}
									value={formDetails.loanProduct}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Loan Product</BaseOption>
									{loanProducts?.map(
										(product: Record<string, any>, index: number) => (
											<BaseOption key={index} value={product.id}>
												{product.name
													?.split(" ")
													.map(
														(word: string) =>
															word.charAt(0).toUpperCase() + word.slice(1),
													)
													.join(" ")}
											</BaseOption>
										),
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Loan ID</BaseLabel>
								<BaseInput
									required
									readOnly
									name="loanId"
									value={formDetails.loanId.toString().padStart(3, "0")}
									placeholder="Loan ID"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Loan Term</BaseLabel>
								<BaseInput
									required
									name="term"
									value={formDetails.term}
									placeholder="Loan Term"
									onChange={(e) => handleChange(e)}
								/>
								{selectedLoanProduct && (
									<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
										<Typography
											variant="caption"
											fontFamily={"Inter"}
											fontWeight={"600"}
											fontSize={14}
											lineHeight={"normal"}
											color={"var(--text-salmon-pink-color)"}
											whiteSpace={"normal"}
										>
											{`Allowable term is a duration (in number of ${
												selectedLoanProduct?.term === "annually"
													? "years"
													: selectedLoanProduct?.term === "monthly"
														? "months"
														: selectedLoanProduct?.term === "weekly"
															? "weeks"
															: "days"
											}) less than or equal to ${selectedLoanProduct?.tenor} ${
												selectedLoanProduct?.term === "annually"
													? "years"
													: selectedLoanProduct?.term === "monthly"
														? "months"
														: selectedLoanProduct?.term === "weekly"
															? "weeks"
															: "days"
											}.`}
										</Typography>
									</Box>
								)}
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Monthly Interest Rate
								</BaseLabel>
								<BaseInput
									required
									name="interestRate"
									value={formDetails.interestRate}
									placeholder="Enter Interest Rate"
									onChange={(e) => handleChange(e)}
								/>
								{selectedLoanProduct && (
									<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
										<Typography
											variant="caption"
											fontFamily={"Inter"}
											fontWeight={"600"}
											fontSize={14}
											lineHeight={"normal"}
											color={"var(--text-salmon-pink-color)"}
											whiteSpace={"normal"}
										>
											{`Allowable interest rate is a percentage interest (in number) less than or equal to ${selectedLoanProduct?.interestRate}%.`}
										</Typography>
									</Box>
								)}
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Disbursement Date
								</BaseLabel>
								<BaseInput
									required
									type="date"
									name="disbursementDate"
									value={formDetails.disbursementDate}
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									First Repayment Date
								</BaseLabel>
								<BaseInput
									required
									type="date"
									name="firstRepaymentDate"
									value={formDetails.firstRepaymentDate}
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Applied Amount</BaseLabel>
								<BaseInput
									required
									name="appliedAmount"
									placeholder="Enter Applied Amount"
									value={formDetails.appliedAmount}
									onChange={(e) => handleChange(e)}
								/>
								{selectedLoanProduct && (
									<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
										<Typography
											variant="caption"
											fontFamily={"Inter"}
											fontWeight={"600"}
											fontSize={14}
											lineHeight={"normal"}
											color={"var(--text-salmon-pink-color)"}
											whiteSpace={"normal"}
										>
											{`Allowable application amount is a figure (in number) less than or equal to ${formatAmountDisplay(
												selectedLoanProduct?.amount ?? 0,
											)}.`}
										</Typography>
									</Box>
								)}
							</BaseFieldSet>
						</Grid2>
						{/* <Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Penalty</BaseLabel>
								<BaseInput
									required
									name="penalty"
									placeholder="Enter Penalty"
									value={formDetails.penalty}
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2> */}
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Attachments</BaseLabel>
								<BaseInput
									type="file"
									name="attachments"
									inputRef={fileInputRef}
									inputProps={{
										accept:
											".jpeg,.jpg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx,.rtf,.odt,.ods,.odp",
										multiple: true,
									}}
									onChange={handleChange}
								/>
								{formDetails.attachments.length > 0 && (
									<Stack
										direction={"row"}
										flexWrap={"wrap"}
										gap={"calc(var(--flex-gap)/4)"}
										marginBlockStart={"calc(var(--basic-margin)/4)"}
									>
										{formDetails.attachments.map((file: File, index) => (
											<Chip
												key={index}
												label={file.name}
												onDelete={() => handleDeleteAttachment(file)}
											/>
										))}
									</Stack>
								)}
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Description</BaseLabel>
								<BaseInput
									required
									multiline
									minRows={4}
									name="description"
									value={formDetails.description}
									placeholder="Add any additional details or notes."
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
					</Grid2>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box overflow={"hidden"}>
						<BaseButton
							type="submit"
							variant="contained"
							bgcolor="var(--primary-color)"
							disableElevation
							sx={{
								width: { mobile: "100%", miniTablet: "auto" },
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Submit
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
			</DraftLoanApplicationWrapper>
		</AppLayout>
	);
};
